import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/api';
import { MdOutlineBlock } from 'react-icons/md';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { toast } from 'sonner';

const Transactions = () => {
  const [transactions, setTransactions] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchTransaction, setSearchTransaction] = useState('');

  // Fetch All transactions
  const getTransaction = useCallback(async () => {
    try {
      const response = await axios.get(`/transaction?page_number=${currentPage}&page_size=${recordsPerPage}`);
      setTransactions(response.data.Data);
      setTotal(response.data.Total);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setError(error);
      setLoading(false);
    }
  }, [currentPage, recordsPerPage]);

  // Search transaction
  const handleSearchChange = (e) => {
    setSearchTransaction(e.target.value);
    setCurrentPage(1);
  };

  // debounce for search query
  useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getTransaction(currentPage * recordsPerPage, recordsPerPage, searchTransaction);
    }, 500);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, searchTransaction, getTransaction]);

  // reverse a transaction
  const reverse = async (transaction_id) => {
    const confirmed = window.confirm('Are you sure you want to reverse this transaction?');
    if (confirmed) {
      try {
        await axios.put(`/request_transaction/${transaction_id}`);
        toast.success('Reversal initiated!');
        getTransaction();
      } catch (error) {
        toast.error('Failed to initiate reversal');
        console.log(error);
      }
    }
  };

  return (
    <div className='mx-auto p-4'>
      <div className='bg-white rounded-lg p-4 border border-[#e0e2e7] lg:w-[78vw] xl:w-[81vw] 2xl:w-full'>
        <div className='flex flex-wrap items-center justify-between py-3'>
          <h5 className='text-[#fb0000]'>Showing {transactions.length} of {total} transactions</h5>
          <div className='py-2'>
            <form>
              <label htmlFor='search'><span className='hidden'>Search</span>
                <input
                  type='search'
                  id='search'
                  className='px-3 py-2 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#fb0000] focus:ring-[#fb0000] focus:ring-1'
                  required
                  placeholder='Search transaction'
                  value={searchTransaction}
                  onChange={handleSearchChange}
                />
              </label>
            </form>
          </div>
        </div>
        {loading
          ? (
            <div className='flex items-center justify-center bg-[#f1f4f6] h-screen md:h-[56em] lg:h-[25em] xl:h-[35em] 2xl:lg:h-[48em]'>
              <Loader />
            </div>
            )
          : error
            ? (
              <div className='bg-[#f1f4f6] grid place-items-center h-screen md:h-[56em] lg:h-[25em] xl:h-[35em] 2xl:lg:h-[48em]'>
                <div className='grid place-items-center'>
                  <h2><MdOutlineBlock /></h2>
                  <h4>Error occurred while fetching data</h4>
                </div>
              </div>
              )
            : (
              <div className='overflow-x-auto '>
                {transactions.length > 0
                  ? (
                    <table className='w-full text-left table-auto'>
                      <thead>
                        <tr className='border-b border-slate-500'>
                          <th className='px-2 py-3'>Account Number</th>
                          <th className='px-2 py-3'>Balance Before</th>
                          <th className='px-2 py-3'>Balance After</th>
                          <th className='px-2 py-3'>Debit</th>
                          <th className='px-2 py-3'>Credit</th>
                          <th className='px-2 py-3'>Transaction Status</th>
                          <th className='px-2 py-3'>Time Created</th>
                          <th className='px-2 py-3'>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction) => (
                          <tr key={transaction.transaction_id}>
                            <td className='p-1.5'>{transaction.account_number}</td>
                            <td className='p-1.5'>{transaction.balance_before}</td>
                            <td className='p-1.5'>{transaction.balance_after}</td>
                            <td className='p-1.5'>{transaction.debit}</td>
                            <td className='p-1.5'>{transaction.credit}</td>
                            <td className='p-1.5'>{transaction.transaction_status}</td>
                            <td className='p-1.5'>{new Date(transaction.transaction_timestamp).toISOString().replace('T', ' ').slice(0, 19)}</td>
                            <td className='p-1.5'><button onClick={() => reverse(transaction.transaction_id)} className='border border-[#fb0000] text-[#fb0000] font-bold px-5 py-0.5 rounded-full hover:bg-[#fb0000] hover:text-white'>Reverse</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    )
                  : (
                    <div className='bg-[#f1f4f6] grid place-items-center h-screen md:h-[56em] lg:h-[25em] xl:h-[35em] 2xl:lg:h-[48em]'>
                      <div className='grid place-items-center'>
                        <h2><MdOutlineBlock /></h2>
                        <h4>No Data</h4>
                      </div>
                    </div>
                    )}
              </div>
              )}
        <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
          <div>
            <span className='mr-2'>Records per page:</span>
            <select
              className='px-3 py-1 border bg-[#f2f9ff] border-slate-300 rounded-md focus:outline-none focus:border-[#85d6e3] focus:ring-[#85d6e3]'
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
            >
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='75'>75</option>
              <option value='100'>100</option>
            </select>
          </div>
          <Pagination
            nPages={Math.ceil(total / recordsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default Transactions;
